import { timestampDate } from '@bufbuild/protobuf/wkt';
import {
  DeleteMode,
  useCheckDeploymentDeleteBlockers,
  useDeleteDeployment,
  useGetDeployment,
  useGetHealthTimeline,
  useGetLastDeploymentWorkflowRun,
  useInfiniteListAuditEvents,
  useListWorkspaces,
  useStreamDeploymentEvents,
  useStreamReleaseEvents,
} from '@sunstone/api-client/hooks';
import {
  Alert,
  AlertDescription,
  Card,
  CardContent,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@sunstone/ui';
import { formatDateTime } from '@sunstone/utils';
import { ExternalLink, Info } from 'lucide-react';
import { useState } from 'react';
import { useNavigate } from 'react-router';
import { AuditHistory } from '@/components/ui/AuditHistory';
import { DeleteConfirmationDialog } from '@/components/ui/DeleteConfirmationDialog';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { WorkflowRunBanner } from '@/components/ui/WorkflowRunBanner';
import { useAuthToken } from '@/features/auth';
import { type Blip, InlineHealthStrip } from '@/features/health/HealthBlips';
import { WorkspaceList } from '@/features/workspaces/WorkspaceList';
import { BootstrapInstructions } from './BootstrapInstructions';
import { deploymentModelKey, deploymentStatusKey, deploymentTypeKey } from './deploymentFormatters';

const CLOUD_LABELS: Record<string, string> = {
  DEPLOYMENT_TYPE_CLOUD_AWS: 'Amazon Web Services',
  DEPLOYMENT_TYPE_CLOUD_GCP: 'Google Cloud Platform',
  DEPLOYMENT_TYPE_CLOUD_AZURE: 'Microsoft Azure',
  DEPLOYMENT_TYPE_ON_PREM: 'On-Premises',
};

const MODEL_LABELS: Record<string, string> = {
  DEPLOYMENT_MODEL_HYBRID: 'Hybrid — Uniphore-Managed, Customer Infrastructure',
  DEPLOYMENT_MODEL_SAAS: 'SaaS — Uniphore-Managed, Uniphore Infrastructure',
  DEPLOYMENT_MODEL_AIRGAPPED: 'Airgapped — Customer-Managed, Customer Infrastructure',
};

const AWAITING_BANNER =
  'Sunstone is waiting to receive the bootstrap callback from your cloud account. This happens automatically after your CloudFormation stack reaches CREATE_COMPLETE status. No action is required unless this persists for more than 10 minutes.';

interface DeploymentDetailProps {
  deploymentId: string;
  deleteOpen?: boolean;
  onDeleteOpenChange?: (open: boolean) => void;
}

export function DeploymentDetail({
  deploymentId,
  deleteOpen: propDeleteOpen,
  onDeleteOpenChange,
}: DeploymentDetailProps) {
  const { token } = useAuthToken();
  const navigate = useNavigate();

  useStreamReleaseEvents(token);
  useStreamDeploymentEvents(deploymentId, token);
  const { data, isLoading, error } = useGetDeployment(deploymentId, token);
  const { data: lastWorkflowRunData } = useGetLastDeploymentWorkflowRun(deploymentId, token);
  const lastWorkflowRun = lastWorkflowRunData?.found ? lastWorkflowRunData : undefined;
  const {
    data: auditData,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteListAuditEvents(token, 'deployment', deploymentId);
  const auditEvents = auditData?.pages.flatMap((p) => p.events) ?? [];
  const deployment = data?.deployment;

  const { data: workspaceCountData } = useListWorkspaces(deploymentId, token, { pageSize: 1 });
  const workspaceCount = workspaceCountData?.totalCount ?? 0;

  const [internalDeleteOpen, setInternalDeleteOpen] = useState(false);
  const deleteOpen = propDeleteOpen ?? internalDeleteOpen;
  const setDeleteOpen = onDeleteOpenChange ?? setInternalDeleteOpen;
  const [isDeleting, setIsDeleting] = useState(false);

  const { data: blockersData } = useCheckDeploymentDeleteBlockers(deploymentId, token, deleteOpen);
  const blockingResources = (blockersData?.blockingResources ?? []).map((r) => ({
    id: r.id,
    displayName: r.displayName,
    resourceType: r.resourceType,
  }));

  const deleteDeployment = useDeleteDeployment(token);

  const toHour = new Date();
  toHour.setMinutes(0, 0, 0);
  const { data: healthData, isLoading: healthLoading } = useGetHealthTimeline(token, {
    id: deploymentId,
    level: 'sunstone_deployment',
    from: new Date(toHour.getTime() - 24 * 60 * 60 * 1000).toISOString(),
    to: toHour.toISOString(),
    grain: 'hour',
    overallOnly: false,
  });

  function handleDelete(mode: 'full' | 'force_tombstone') {
    setIsDeleting(true);
    const deleteMode = mode === 'force_tombstone' ? DeleteMode.FORCE_TOMBSTONE : DeleteMode.FULL;
    deleteDeployment.mutate(
      { id: deploymentId, mode: deleteMode },
      {
        onSuccess: () => {
          setDeleteOpen(false);
          if (deployment?.tenantId) {
            navigate(`/tenants/${deployment.tenantId}`);
          } else {
            navigate('/deployments');
          }
        },
        onSettled: () => setIsDeleting(false),
      },
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">Loading...</div>
    );
  }

  if (error || !deployment) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground">
        Deployment not found.
      </div>
    );
  }

  const statusKey = deploymentStatusKey(deployment.status);
  const isAwaitingBootstrap = statusKey === 'DEPLOYMENT_STATUS_AWAITING_BOOTSTRAP';
  const isCreated = statusKey === 'DEPLOYMENT_STATUS_CREATED';
  const typeKey = deploymentTypeKey(deployment.deploymentType);
  const modelKey = deploymentModelKey(deployment.deploymentModel);

  const overallBlips: Blip[] = healthData?.overall ?? [];
  const workspaceBlipsByEntityId: Record<string, Blip[]> = Object.fromEntries(
    (healthData?.subtotals ?? [])
      .filter((s) => s.level === 'sunstone_workspace')
      .map((s) => [s.id, s.blips]),
  );

  return (
    <div className="space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500">
      <WorkflowRunBanner run={lastWorkflowRun} />

      {/* Awaiting-bootstrap banner */}
      {isAwaitingBootstrap && (
        <Alert className="border-blue-200 bg-blue-50/50 dark:border-blue-900 dark:bg-blue-950/50">
          <Info className="h-4 w-4 text-blue-600 dark:text-blue-400" />
          <AlertDescription className="text-muted-foreground">{AWAITING_BANNER}</AlertDescription>
        </Alert>
      )}

      {/* Metadata strip */}
      <Card>
        <CardContent className="py-4">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">URL Name</p>
              <p className="text-sm font-mono text-foreground">{deployment.slug || '—'}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Cloud Provider</p>
              <p className="text-sm text-foreground">{CLOUD_LABELS[typeKey] ?? typeKey}</p>
            </div>
            {deployment.region && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 font-medium">Region</p>
                <p className="text-sm font-mono text-foreground">{deployment.region}</p>
              </div>
            )}
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Status</p>
              <StatusBadge status={statusKey} />
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Workspaces</p>
              <p className="text-sm text-foreground">{workspaceCount}</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-xs text-muted-foreground mb-1 font-medium">Deployment Model</p>
              <p className="text-sm text-foreground">{MODEL_LABELS[modelKey] ?? modelKey}</p>
            </div>
            <div>
              <p className="text-xs text-muted-foreground mb-1 font-medium">Created</p>
              <p className="text-sm text-foreground">
                {deployment.createdAt ? formatDateTime(timestampDate(deployment.createdAt)) : '—'}
              </p>
            </div>
            {deployment.duploPortalUrl && !isCreated && !isAwaitingBootstrap && (
              <div>
                <p className="text-xs text-muted-foreground mb-1 font-medium">Duplo Portal</p>
                <a
                  href={deployment.duploPortalUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                >
                  Open in Duplo
                  <ExternalLink className="w-3.5 h-3.5" />
                </a>
              </div>
            )}
            {!isCreated && (
              <div>
                <InlineHealthStrip blips={overallBlips} isLoading={healthLoading} />
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bootstrap instructions (CREATED state) */}
      {isCreated && <BootstrapInstructions deploymentType={typeKey} />}

      {/* Tabs: Workspaces + History */}
      {!isCreated && (
        <Tabs defaultValue="workspaces">
          <TabsList>
            <TabsTrigger value="workspaces">
              Workspaces
              {workspaceCount > 0 && (
                <span className="ml-1.5 text-xs bg-muted-foreground/20 rounded-full px-1.5 py-0.5">
                  {workspaceCount}
                </span>
              )}
            </TabsTrigger>
            <TabsTrigger value="history">History</TabsTrigger>
          </TabsList>
          <TabsContent value="workspaces" className="mt-6 animate-in fade-in duration-300">
            <WorkspaceList
              deploymentId={deploymentId}
              tenantId={deployment.tenantId}
              healthBlipsByEntityId={workspaceBlipsByEntityId}
              healthLoading={healthLoading}
            />
          </TabsContent>
          <TabsContent value="history" className="mt-6 animate-in fade-in duration-300">
            <AuditHistory
              events={auditEvents}
              onLoadMore={fetchNextPage}
              hasMore={hasNextPage}
              isLoadingMore={isFetchingNextPage}
            />
          </TabsContent>
        </Tabs>
      )}

      <DeleteConfirmationDialog
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        resourceName={deployment.displayName}
        resourceType="deployment"
        blockingResources={blockingResources}
        supportsForceTombstone={true}
        onConfirm={handleDelete}
        isDeleting={isDeleting}
      />
    </div>
  );
}
